import React, { KeyboardEvent } from 'react';
import { Button } from '~/UIKit';
import { KeyboardArrowLeft } from '@mui/icons-material';
import { useLanguage, Navigate, useNavigation } from '~/Hooks';
import { useParams } from 'react-router-dom';
import classnames from 'classnames';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';

interface BackButtonContainerProps {
  to: string;
  slim?: boolean;
  onBack?: () => void;
  text?: string;
}

const useStyles = makeStyles(
  () => ({
    root: {
      fontSize: FONT_SIZE_SMALLISH,
      fontWeight: 'bold',
      minWidth: 60,
      height: 35,
    },
    icon: {
      marginLeft: -12,
      marginRight: -4,
      '& > *:nth-of-type(1)': {
        fontSize: 40,
      },
    },
    slim: {
      height: 22,
      paddingLeft: 0,
      marginBottom: 4,
      '& > span': {
        marginRight: 0,
        '& > *:nth-of-type(1)': {
          fontSize: 22,
        },
      },
    },
  }),
  { name: 'BackButton' },
);

const handleNavigation = (navigate: Navigate, domain: string) => (url: string, params?: any) =>
  navigate(url, { domain, ...params });

interface RouteParams {
  domain: string;
}

const useBackButton = (to: string, onBack?: () => void) => {
  const { navigate, history, goBack } = useNavigation();
  const params = useParams<RouteParams>();

  const handleGoBack = (to: string) => {
    if (history.length > 1) {
      goBack();
    } else {
      handleNavigation(navigate, params.domain)(to);
    }
  };

  const handleClick = () => {
    if (onBack) {
      onBack();
    } else {
      handleGoBack(to);
    }
  };

  const handleBackspace = (e: KeyboardEvent) => {
    /*
      https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/click-events-have-key-events.md
      Click events on non standard elements require a keyboard listener for accessibility reasons :D
      Key 8 is Backspace :P
    */
    if (e.key === '8') {
      handleGoBack(to);
    }
  };

  return {
    handleBackspace,
    handleClick,
  };
};

export const BackButton = ({ to, slim, onBack, text }: BackButtonContainerProps) => {
  const { handleClick, handleBackspace } = useBackButton(to, onBack);
  const { t } = useLanguage();
  const { classes } = useStyles();

  return (
    <Button
      classes={{ root: classes.root, startIcon: classes.icon }}
      className={classnames({ [classes.slim]: slim })}
      onClick={handleClick}
      onKeyUp={handleBackspace}
      startIcon={<KeyboardArrowLeft />}
    >
      {text || t('app.back')}
    </Button>
  );
};
